import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Search, Trash2, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["all", "one_time", "recurring", "credits"];
const CATEGORIES = ["", "Hosting", "Support", "Development", "License", "Subscription", "Consulting", "Design", "Other"];

function ArticleForm({ data, onChange, onSubmit, submitLabel }) {
  const set = (field, value) => onChange({ ...data, [field]: value });
  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <input required placeholder="Article name" value={data.name} onChange={e => set("name", e.target.value)} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="article-name-input" />
      <div className="grid grid-cols-2 gap-3">
        <input placeholder="Article number / SKU" value={data.article_number} onChange={e => set("article_number", e.target.value)} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
        <select value={data.category} onChange={e => set("category", e.target.value)} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
          <option value="">No category</option>
          {CATEGORIES.filter(Boolean).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <textarea placeholder="Description" value={data.description} onChange={e => set("description", e.target.value)} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" rows={2} />
      <div className="grid grid-cols-5 gap-3">
        <div>
          <label className="font-mono text-[10px] text-[#857973] mb-1 block">PRICE</label>
          <input type="number" step="0.01" required value={data.unit_price} onChange={e => set("unit_price", e.target.value)} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="article-price-input" />
        </div>
        <div>
          <label className="font-mono text-[10px] text-[#857973] mb-1 block">CURRENCY</label>
          <select value={data.currency || "EUR"} onChange={e => set("currency", e.target.value)} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
            <option value="EUR">EUR</option>
            <option value="SEK">SEK</option>
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] text-[#857973] mb-1 block">UNIT</label>
          <select value={data.unit || "piece"} onChange={e => set("unit", e.target.value)} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
            <option value="piece">Piece</option>
            <option value="credit">Credit</option>
            <option value="hour">Hour</option>
            <option value="month">Month</option>
            <option value="license">License</option>
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] text-[#857973] mb-1 block">STOCK</label>
          <input type="number" value={data.stock ?? ""} onChange={e => set("stock", e.target.value)} placeholder="∞" className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="article-stock" />
        </div>
        <div>
          <label className="font-mono text-[10px] text-[#857973] mb-1 block">TYPE</label>
          <select value={data.type} onChange={e => set("type", e.target.value)} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="article-type-select">
            <option value="one_time">One-time</option>
            <option value="recurring">Recurring</option>
            <option value="credits">Credits</option>
          </select>
        </div>
      </div>
      {data.type === "recurring" && (
        <div>
          <label className="font-mono text-[10px] text-[#857973] mb-1 block">INTERVAL</label>
          <select value={data.billing_interval} onChange={e => set("billing_interval", e.target.value)} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      )}
      <button type="submit" data-testid="article-submit-btn" className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium">{submitLabel}</button>
    </form>
  );
}

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", article_number: "", unit_price: 0, currency: "EUR", unit: "piece", stock: "", type: "one_time", billing_interval: "", category: "", active: true });

  const fetchArticles = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/articles`, { params: { type: filterType }, withCredentials: true });
    setArticles(data);
  }, [filterType]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const filtered = articles.filter(a =>
    !search || a.name?.toLowerCase().includes(search.toLowerCase()) || a.article_number?.toLowerCase().includes(search.toLowerCase())
  );

  const resetForm = () => setForm({ name: "", description: "", article_number: "", unit_price: 0, currency: "EUR", unit: "piece", stock: "", type: "one_time", billing_interval: "", category: "", active: true });

  const createArticle = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/articles`, { ...form, unit_price: Number(form.unit_price), stock: form.stock !== "" ? Number(form.stock) : null }, { withCredentials: true });
    setShowCreate(false);
    resetForm();
    fetchArticles();
  };

  const saveEdit = async () => {
    if (!editArticle) return;
    await axios.put(`${API}/admin/articles/${editArticle.id}`, {
      name: editArticle.name, description: editArticle.description, article_number: editArticle.article_number,
      unit_price: Number(editArticle.unit_price), currency: editArticle.currency, unit: editArticle.unit,
      stock: editArticle.stock !== "" && editArticle.stock != null ? Number(editArticle.stock) : null,
      type: editArticle.type, billing_interval: editArticle.billing_interval, category: editArticle.category, active: editArticle.active,
    }, { withCredentials: true });
    setEditArticle(null);
    fetchArticles();
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await axios.delete(`${API}/admin/articles/${id}`, { withCredentials: true });
    fetchArticles();
    setEditArticle(null);
  };

  return (
    <div data-testid="articles-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Articles</h1>
          <p className="text-sm text-[#857973]">{articles.length} articles</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-article-btn" className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
          <Plus className="w-4 h-4" /> New Article
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
          <input data-testid="articles-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..."
            className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
        </div>
        <div className="flex gap-1">
          {TYPES.map(t => (
            <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterType === t ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>{t.replace("_", " ")}</button>
          ))}
        </div>
      </div>

      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1E3050]">
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">ARTICLE</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">SKU</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">CATEGORY</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">PRICE</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">UNIT</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">STOCK</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">TYPE</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">STATUS</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => (
              <tr key={a.id} className="border-b border-[#1E3050] hover:bg-[#1E3050]/20 cursor-pointer transition-colors" onClick={() => setEditArticle({...a})}>
                <td className="px-4 py-3">
                  <span className="text-sm text-white">{a.name}</span>
                  {a.description && <span className="text-xs text-[#857973] block">{a.description.slice(0, 50)}</span>}
                </td>
                <td className="px-4 py-3 text-xs text-[#857973] font-mono">{a.article_number || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#857973]">{a.category || "—"}</td>
                <td className="px-4 py-3 text-sm text-white font-mono">{Number(a.unit_price).toLocaleString()} {a.currency || "EUR"}</td>
                <td className="px-4 py-3 text-xs text-[#857973]">{a.unit || "piece"}</td>
                <td className="px-4 py-3 text-sm text-[#857973] font-mono">{a.stock != null ? a.stock : "∞"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${a.type === "recurring" ? "bg-purple-500/10 text-purple-400" : "bg-blue-500/10 text-blue-400"}`}>
                    {a.type?.replace("_", " ")}{a.billing_interval ? ` / ${a.billing_interval}` : ""}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${a.active ? "bg-green-500/10 text-green-400" : "bg-neutral-500/10 text-[#857973]"}`}>{a.active ? "active" : "inactive"}</span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={(e) => { e.stopPropagation(); deleteArticle(a.id); }} className="text-[#857973] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-[#857973]">No articles found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCreate(false)} />
          <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-lg p-6 z-10">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-outfit font-bold text-lg text-white">Create Article</h2>
              <button onClick={() => setShowCreate(false)} className="text-[#857973] hover:text-white text-xl">&times;</button>
            </div>
            <ArticleForm data={form} onChange={setForm} onSubmit={createArticle} submitLabel="Create Article" />
          </div>
        </div>
      )}

      {/* Edit sheet */}
      {editArticle && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditArticle(null)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0F1F3A] border-l border-[#1E3050] overflow-y-auto z-10 p-6">
            <div className="flex justify-between mb-6">
              <h2 className="font-outfit font-bold text-xl text-white">Edit Article</h2>
              <div className="flex gap-2">
                <button onClick={() => deleteArticle(editArticle.id)} className="text-[#857973] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                <button onClick={() => setEditArticle(null)} className="text-[#857973] hover:text-white text-xl">&times;</button>
              </div>
            </div>
            <ArticleForm data={editArticle} onChange={setEditArticle} onSubmit={(e) => { e.preventDefault(); saveEdit(); }} submitLabel="Save Changes" />
          </div>
        </div>
      )}
    </div>
  );
}
